import React, { useState, useEffect } from "react";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from "@material-ui/core";
import _ from "lodash";
import { useDispatch } from "react-redux";
import { useRouteMatch } from "react-router-dom";
import { createWrapper } from "../../../../../../services/menuApp/menuService";
import { updateDirectoryInStore } from "../../../../../../redux/actions/menu";
import DialogCommonDefault from "../../../../../../components/dialogs/DialogCommonDefault";
import {
  ERROR_MESSAGE_UNEXPECTED_ERROR,
  SUCCESSFULLY_CREATED,
} from "../../../../../../utils/consts";
import ColorSelection from "../../../../../PrestoExpressEatprestoApp/pages/ThemeCustomizationPage/ColorSelection";
import TextfieldDefaultNew from "../../../../../../components/textField/TextfieldDefaultNew";
import ButtonCommon from "../../../../../../components/buttons/ButtonCommon";

export interface DirectoryAddModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  handleCloseDirectoryAddModal: () => void;
  categoryId: any;
  directoryId: any;
  directoryLevel: number;
  menuNodeList: any;
  menuId: string;
  firstWrapperId: any;
  setMenuNodeList: (list: any) => void;
  setSuccess: (message: string) => void;
  setError: (message: string) => void;
  isRoot: boolean;
  setIsOpenDirectoryOrProductSelectionModal: (open: boolean) => void;
  onItemAdded?: (newItem?: any) => void;
  wrapperId?: any;
}

const DirectoryAddModal: React.FunctionComponent<DirectoryAddModalProps> = ({
  isOpen,
  setIsOpen,
  handleCloseDirectoryAddModal,
  categoryId,
  directoryId,
  directoryLevel,
  menuNodeList,
  menuId,
  firstWrapperId,
  setMenuNodeList,
  setSuccess,
  setError,
  isRoot,
  setIsOpenDirectoryOrProductSelectionModal,
  onItemAdded,
  wrapperId,
}) => {
  const match: any = useRouteMatch();
  const dispatch = useDispatch();

  // Form state
  const [directoryName, setDirectoryName] = useState("");
  const [url, setUrl] = useState("");
  const [color, setColor] = useState("");
  const [isDirectoryNameEmpty, setIsDirectoryNameEmpty] = useState(false);
  const [isLoadingButton, setIsLoadingButton] = useState(false);

  // Helper function to extract ID from categoryId (handles both number and object)
  const getCategoryIdValue = (catId: any): number => {
    if (!catId) return 0;
    if (typeof catId === "number") return catId;
    if (typeof catId === "object" && catId.id) return catId.id;
    return 0;
  };

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setDirectoryName("");
      setUrl("");
      setColor("");
      setIsDirectoryNameEmpty(false);
    }
  }, [isOpen]);

  // Function to recursively find and add directory to nested structure
  const findAndAddDirectory = (
    node: any,
    targetId: number,
    newDirectory: any,
  ): boolean => {
    if (!node) return false;

    // Check if current node is the target parent directory
    if (node.id === targetId) {
      if (!node.wrapper) {
        node.wrapper = {};
      }
      node.wrapper[newDirectory.id] = newDirectory;
      return true;
    }

    // Recursively search in wrapper children
    if (node.wrapper) {
      for (const key in node.wrapper) {
        if (findAndAddDirectory(node.wrapper[key], targetId, newDirectory)) {
          return true;
        }
      }
    }
    return false;
  };

  const handleDirectoryAddInfo = async () => {
    if (!directoryName.trim()) {
      setIsDirectoryNameEmpty(true);
      return;
    }

    setIsLoadingButton(true);

    // Get the actual category ID value (handles both number and object)
    const actualCategoryId = getCategoryIdValue(categoryId);

    // Correct body structure for directory creation
    const body = {
      name: directoryName,
      locationId: match.params.locationId,
      categoryId: actualCategoryId,
      nameShort: "",
      directory: true,
      img: url || "",
      colour: color || "",
      directoryId: directoryId || "",
      directoryLevel: directoryLevel || 1,
    };

    try {
      const summaryString = `${directoryName} Directory is added`;

      const res = await createWrapper(
        match.params.locationId,
        body,
        "Menus Changed",
        summaryString,
        "menu",
      );

      // Get the newly created directory from response and normalize categoryId
      let newDirectory = res.data.data;
      if (
        newDirectory.categoryId &&
        typeof newDirectory.categoryId === "object"
      ) {
        newDirectory = {
          ...newDirectory,
          categoryId: newDirectory.categoryId.id,
        };
      }

      // Update Redux store directly with the response data
      const newDirectoryForRedux = {
        id: newDirectory.id,
        valid: newDirectory.valid,
        version: newDirectory.version,
        name: newDirectory.name,
        colour: newDirectory.colour,
        img: newDirectory.img,
        desc: newDirectory.desc,
        directory: newDirectory.directory,
        directoryId: newDirectory.directoryId,
        directoryLevel: newDirectory.directoryLevel,
        itemType: newDirectory.itemType,
        allYouCanEat: newDirectory.allYouCanEat,
        kiosk: newDirectory.kiosk,
        cover: newDirectory.cover,
        nameShort: newDirectory.nameShort,
        viewOrder: newDirectory.viewOrder,
      };

      dispatch(updateDirectoryInStore(newDirectoryForRedux));

      const cloneMenuNode = _.cloneDeep(menuNodeList);

      if (isRoot) {
        if (!cloneMenuNode[menuId]?.category[actualCategoryId]?.wrapper) {
          if (!cloneMenuNode[menuId]) cloneMenuNode[menuId] = {};
          if (!cloneMenuNode[menuId].category)
            cloneMenuNode[menuId].category = {};
          if (!cloneMenuNode[menuId].category[actualCategoryId])
            cloneMenuNode[menuId].category[actualCategoryId] = {};
          if (!cloneMenuNode[menuId].category[actualCategoryId].wrapper) {
            cloneMenuNode[menuId].category[actualCategoryId].wrapper = {};
          }
        }
        cloneMenuNode[menuId].category[actualCategoryId].wrapper[
          newDirectory.id
        ] = newDirectory;
      } else {
        const updateProductName = (node: any) => {
          // Check if the current node is a product and has the specified ID
          if (node.id === wrapperId) {
            if (_.isEmpty(node["wrapper"])) {
              node["wrapper"] = {};
            }
            node["wrapper"][newDirectory.id] = newDirectory;
            return true;
          }

          // Recursively traverse nested wrappers
          if (node.wrapper) {
            for (const key in node.wrapper) {
              if (Object.hasOwnProperty.call(node.wrapper, key)) {
                const child = node.wrapper[key];
                if (updateProductName(child)) {
                  return true;
                }
              }
            }
          }
          return false;
        };

        // Find and update the product name
        if (
          cloneMenuNode[menuId] &&
          cloneMenuNode[menuId].category[actualCategoryId]
        ) {
          updateProductName(
            cloneMenuNode[menuId].category[actualCategoryId].wrapper[
              firstWrapperId
            ],
          );
        }
      }

      setMenuNodeList(cloneMenuNode);
      setIsDirectoryNameEmpty(false);
      setIsLoadingButton(false);
      setIsOpen(false);
      setIsOpenDirectoryOrProductSelectionModal(false);
      setSuccess(SUCCESSFULLY_CREATED);

      if (onItemAdded) {
        onItemAdded(newDirectory);
      }
    } catch (error) {
      console.error("DirectoryAddModal Error:", error);
      setIsLoadingButton(false);
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
    }
  };

  return (
    <DialogCommonDefault
      open={isOpen}
      setOpen={setIsOpen}
      isNeedFixedHeight={false}
    >
      <DialogTitle>Add Directory</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography style={{ paddingLeft: "4px" }}>
              Directory Name
            </Typography>
            <TextfieldDefaultNew
              id="directory-name"
              name="directory-name"
              onChange={(e: any) => {
                setIsDirectoryNameEmpty(false);
                setDirectoryName(e.target.value);
              }}
              value={directoryName}
              type="text"
            />
            {isDirectoryNameEmpty && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "start",
                  marginBottom: "3px",
                }}
              >
                <Typography
                  align="left"
                  variant="caption"
                  style={{ color: "red" }}
                >
                  Please enter directory name.
                </Typography>
              </div>
            )}
          </Grid>
          <Grid item xs={12}>
            <Typography style={{ paddingLeft: "4px" }}>Image URL</Typography>
            <TextfieldDefaultNew
              id="directory-url"
              name="directory-url"
              onChange={(e: any) => {
                setUrl(e.target.value);
              }}
              value={url}
              type="text"
            />
          </Grid>

          <Grid item xs={12} style={{ marginTop: "4px" }}>
            <Typography style={{ paddingLeft: "4px" }}>Color</Typography>
            <ColorSelection
              title={"Color"}
              colorCode={color}
              setColor={(colorVal: any) => setColor(colorVal.hex)}
              setInitialData={() => setColor("")}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions style={{ marginBottom: "8px" }}>
        <ButtonCommon
          variant="contained"
          style={{ fontSize: 11, width: "100px", marginRight: 8 }}
          color="yellow"
          onClick={handleCloseDirectoryAddModal}
        >
          Cancel
        </ButtonCommon>
        <ButtonCommon
          variant="contained"
          style={{
            fontSize: 11,
            marginLeft: 4,
            width: "100px",
          }}
          isLoadingPage={isLoadingButton}
          color={"orange"}
          onClick={handleDirectoryAddInfo}
        >
          {"Save"}
        </ButtonCommon>
      </DialogActions>
    </DialogCommonDefault>
  );
};

export default DirectoryAddModal;
